import type { IWithdrawalService } from '@core-interfaces/services/withdrawal';
import { Injectable } from '@angular/core';
import type { IProviderWithdrawalRequest, IPaginatedWithdrawals } from '@core-interfaces/dtos/withdrawal';
import { map, Observable, Subject } from 'rxjs';
import { Microservice } from '@core-interfaces/global/microservice';
import { ApiSuccessResponse } from '@core-interfaces/global/http';
import { HttpClient, HttpParams } from '@angular/common/http';

@Injectable({ providedIn: 'root' })
export class WithdrawalService implements IWithdrawalService {
  constructor(private http: HttpClient) {}

  private baseUrl = `/${Microservice.backoffice_service}/withdrawals`;

  // Notifica que cambió la cantidad de retiros pendientes de aprobación, para que
  // el badge del menú se recalcule (p. ej. tras aprobar/rechazar una solicitud).
  private pendingBadgeRefresh = new Subject<void>();
  pendingBadgeRefresh$ = this.pendingBadgeRefresh.asObservable();

  notifyPendingBadgeRefresh(): void {
    this.pendingBadgeRefresh.next();
  }

  getWithdrawals(params?: { after?: number; first?: number; statuses?: string[] }): Observable<IPaginatedWithdrawals> {
    let httpParams = new HttpParams();
    if (params?.after !== undefined) httpParams = httpParams.set('after', params.after.toString());
    if (params?.first !== undefined) httpParams = httpParams.set('first', params.first.toString());
    if (params?.statuses?.length) {
      // Se envía como un único parámetro separado por comas para que no se
      // colapse al pasar por el API Gateway (los params repetidos sí se pierden).
      httpParams = httpParams.set('statuses', params.statuses.join(','));
    }
    return this.http
      .get<ApiSuccessResponse<IPaginatedWithdrawals>>(this.baseUrl, { params: httpParams })
      .pipe(map((response) => response.data));
  }

  getWithdrawalById(id: number): Observable<IProviderWithdrawalRequest> {
    return this.http
      .get<ApiSuccessResponse<IProviderWithdrawalRequest>>(`${this.baseUrl}/${id}`)
      .pipe(map((response) => response.data));
  }

  approveWithdrawal(id: number): Observable<IProviderWithdrawalRequest> {
    return this.http
      .put<ApiSuccessResponse<IProviderWithdrawalRequest>>(`${this.baseUrl}/${id}/approve`, {})
      .pipe(map((response) => response.data));
  }

  revertToPending(id: number): Observable<IProviderWithdrawalRequest> {
    return this.http
      .put<ApiSuccessResponse<IProviderWithdrawalRequest>>(`${this.baseUrl}/${id}/revert`, {})
      .pipe(map((response) => response.data));
  }

  confirmWithdrawal(id: number, payload: { admin_notes?: string | null }): Observable<IProviderWithdrawalRequest> {
    return this.http
      .put<ApiSuccessResponse<IProviderWithdrawalRequest>>(`${this.baseUrl}/${id}/confirm`, payload)
      .pipe(map((response) => response.data));
  }

  rejectWithdrawal(
    id: number,
    payload: { rejection_reason: string; admin_notes?: string | null },
  ): Observable<IProviderWithdrawalRequest> {
    return this.http
      .put<ApiSuccessResponse<IProviderWithdrawalRequest>>(`${this.baseUrl}/${id}/reject`, payload)
      .pipe(map((response) => response.data));
  }

  updateAdminNotes(id: number, payload: { admin_notes: string | null }): Observable<IProviderWithdrawalRequest> {
    return this.http
      .put<ApiSuccessResponse<IProviderWithdrawalRequest>>(`${this.baseUrl}/${id}/notes`, payload)
      .pipe(map((response) => response.data));
  }
}
